"use client";
import {
	identifyRybbitUser,
	useClarityAnalytics,
	useUmamiAnalytics,
} from "@analytics";
import { authClient } from "@repo/auth/client";
import { sessionQueryKey, useSessionQuery } from "@saas/auth/lib/api";
import { setAuthHint } from "@saas/auth/lib/auth-hint";
import { reportRegistrationFromBrowser } from "@saas/auth/lib/report-registration";
import { clearSocialAuthMarkers } from "@saas/auth/lib/social-auth-markers";
import { useQueryClient } from "@tanstack/react-query";
import {
	type ReactNode,
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { SessionContext } from "../lib/session-context";

export function SessionProvider({
	children,
}: {
	children: ReactNode;
}) {
	const queryClient = useQueryClient();
	const { trackEvent: trackUmamiEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent } = useClarityAnalytics();
	const previousUserRef = useRef<string | null>(null);

	const { data: sessionData } = useSessionQuery();
	// 查询结果先进自己的 state，用 startTransition 往下传。(saas) layout 用 Suspense 包着正文，
	// 正文还没 hydrate 完时上面的 context 来一次普通更新，React 会把服务器画好的整页正文扔掉、
	// 在浏览器里重画（页面闪一下）；过渡更新会等正文 hydrate 完再应用（10/19 本地冷加载 6/6 次中招）
	const [session, setSession] = useState<typeof sessionData>(undefined);
	// Initialize loaded to false on both server and client to avoid hydration mismatch
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		// Only set loaded to true after mounting on client
		if (sessionData !== undefined) {
			startTransition(() => {
				setSession(sessionData);
				setLoaded(true);
			});
		}
	}, [sessionData]);

	// 营销页首帧靠这个标记预判登录态（见 auth-hint.ts），拿到真实结果就纠正
	useEffect(() => {
		if (loaded) {
			setAuthHint(Boolean(session?.user));
		}
	}, [loaded, session?.user]);

	// 登出是整页跳转，落地页这里拿到空用户就会清掉
	useEffect(() => {
		if (loaded) {
			identifyRybbitUser(session?.user?.id ?? null);
		}
	}, [loaded, session?.user?.id]);

	// The server decides whether OAuth created an account and leaves a short-lived
	// receipt for the browser event. localStorage only identifies an OAuth login;
	// it is no longer trusted to decide signup versus login.
	useEffect(() => {
		const currentUserId = session?.user?.id ?? null;
		if (!loaded || typeof window === "undefined") {
			return;
		}

		const previousUserId = previousUserRef.current;
		const isNewAuthenticatedUser =
			Boolean(currentUserId) && currentUserId !== previousUserId;
		previousUserRef.current = currentUserId;

		void (async () => {
			const registrationStatus = await reportRegistrationFromBrowser();
			if (!isNewAuthenticatedUser) {
				return;
			}

			try {
				const provider = localStorage.getItem("socialAuthProvider");
				if (!provider) {
					return;
				}

				if (registrationStatus === "none") {
					void trackUmamiEvent("login", { method: provider });
					trackClarityEvent("login");
				} else {
					trackClarityEvent("signup");
				}

				clearSocialAuthMarkers();
			} catch (e) {
				console.error("Error tracking OAuth event:", e);
			}
		})();
	}, [session?.user?.id, loaded, trackUmamiEvent, trackClarityEvent]);

	const reloadSession = useCallback(async () => {
		const { data: newSession, error } = await authClient.getSession({
			query: {
				disableCookieCache: true,
			},
		});

		if (error) {
			throw new Error(error.message || "Failed to fetch session");
		}

		queryClient.setQueryData(sessionQueryKey, () => newSession);
	}, [queryClient]);

	// 查询数据一到，本组件会先同步重渲一次；value 固定住，这次就不会往下传
	const value = useMemo(
		() => ({
			loaded,
			session: session?.session ?? null,
			user: session?.user ?? null,
			reloadSession,
		}),
		[loaded, session, reloadSession],
	);

	return (
		<SessionContext.Provider value={value}>
			{children}
		</SessionContext.Provider>
	);
}
